import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { authFetch, API } from '../api';
import { displayLocation, normLocation, parseLocation } from '../location';
import WhoIsWorking from '../pp/WhoIsWorking';

// Warehouse → Calibrate (docs/WAREHOUSE-MODEL.md). One aisle, stop by stop, on Tablet-1/2:
// tap to confirm what is really there — the code on each section, which unit (pallet / metal
// bay) carries it and its setup, gaps (extinguisher, empty space), a section that faces the
// next stop. Everything autosaves; Finish shows what Apply would change, and a manager/admin
// applies it (the picking layout + the unit model, nothing to the shops). Claude, 2026-09-25.

type Side = 'left' | 'right';
type Status = 'ok' | 'wrong' | 'missing' | 'added' | null;
interface Stock { pieces: number; styles: number; articles?: number; top_brand?: string | null }
interface WalkSection { code: string; status: Status; actual: string | null; unit: string | null }
interface WalkGap { side: Side; reason: string; note: string }
interface WalkStop { left: WalkSection[]; right: WalkSection[]; gaps: WalkGap[]; removed?: boolean; added?: boolean; note?: string }
interface WalkUnit { setup: string | null; kind: string | null; turn: string | null; system: string | null; note: string; side: Side | null }
interface Face { letter: string; row: string; kind: 'pallet' | 'metal'; numbering: string; system: string | null; pitch_cm: number }
interface SetupDef { kind: string | null; footprint: number; boxes: number | null }
interface AisleView {
  aisle: { id: string; name: string };
  faces: { left: Face | null; right: Face | null };
  stops: { left: { code: string; stock: Stock; unit_id: string | null; unit_setup: string | null }[];
           right: { code: string; stock: Stock; unit_id: string | null; unit_setup: string | null }[] }[];
  unplaced: { code: string; stock: Stock }[];
  units: Record<string, any>;
  gaps: { stop_index: number; side: Side; reason: string; note: string | null }[];
  setups: Record<string, SetupDef>;
  gap_reasons: string[];
}
interface AisleRow {
  id: string; name: string; stops: number; sections: number; left: string | null; right: string | null;
  kind: { left: string | null; right: string | null }; calibrated_sections: number; calibrated_at: string | null;
  last_walk: { id: number; status: string; actor: string | null; started_at: string; applied_at: string | null } | null;
}

const CARTON_SETUPS = ['S', 'M', 'SL', 'LL', 'ML'];
const METAL_SETUPS = ['tee', 'hoodie', 'tee_special_B'];
const SETUP_LABEL: Record<string, string> = {
  S: 'S', M: 'M', SL: 'SL', LL: 'LL', ML: 'ML·S', tee: 'Tee', hoodie: 'Hoodie', tee_special_B: 'B-special', other: 'Other',
};
const TURN_OF: Record<string, string> = { S: 'long', M: 'long', ML: 'long', SL: 'short', LL: 'short' };

const role = () => localStorage.getItem('wms_role') || 'staff';
const isDevice = () => !!localStorage.getItem('wms_is_device');
const newKey = () => `n${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

async function jget(url: string) {
  const r = await authFetch(url);
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
  return d;
}

// ─── the aisle list ──────────────────────────────────────────────────────────

interface FaceRow {
  letter: string; aisle: string; side: 'left' | 'right'; kind: 'pallet' | 'metal'; system: string | null;
  sections: number; with_unit: number; last_walk: { id: number; status: string; actor: string | null } | null;
}

/** Warehouse → Calibrate: the faces, per aisle. A face's units walk is the main tool (the
 *  sections come from the measurements); "Check codes" is the step-1 per-code walk, kept for
 *  re-checks after a move. */
export function CalibrateAisles() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<AisleRow[] | null>(null);
  const [faces, setFaces] = useState<FaceRow[]>([]);
  const [err, setErr] = useState('');
  useEffect(() => {
    jget(`${API}/warehouse/aisles`).then(d => setRows(d.aisles)).catch(e => setErr(e.message));
    jget(`${API}/warehouse/faces`).then(d => setFaces(d.faces || [])).catch(() => {});
  }, []);
  const faceBtn = (f: FaceRow | undefined) => {
    if (!f) return <div className="flex-1" />;
    const open = f.last_walk && f.last_walk.status === 'open';
    const full = f.sections > 0 && f.with_unit === f.sections;
    return (
      <button onClick={() => navigate(`/warehouse/calibrate/face/${f.letter}`)}
        className={`flex-1 rounded-lg border px-3 py-2 text-left ${open ? 'border-amber-500 bg-amber-500/10'
          : full ? 'border-emerald-700 bg-emerald-900/20' : 'border-gray-700 bg-gray-800/60'} hover:border-blue-500`}>
        <div className="text-xl font-bold text-white">{f.letter}</div>
        <div className="text-xs text-gray-400">{f.kind === 'metal' ? f.system : 'pallets'} · {f.with_unit}/{f.sections}</div>
        {open && <div className="text-xs text-amber-300">open — {f.last_walk!.actor}</div>}
      </button>
    );
  };
  const canApply = role() === 'admin' || role() === 'manager';
  return (
    <div className="p-6 max-w-5xl">
      <button onClick={() => navigate('/settings/warehouse/tools')} className="text-gray-400 hover:text-white text-sm">← Settings · Warehouse</button>
      <h1 className="text-xl font-semibold text-white mb-1">Calibrate the warehouse</h1>
      <p className="text-sm text-gray-400 mb-4">
        The sections come from the measurements (25.9). Walk a face unit by unit and record what stands there:
        for a metal bay the system, shelves, level heights and bracing or beams; for a pallet its setup.
        The number under each letter is units recorded / sections.
      </p>
      {canApply && (
        <button onClick={() => navigate('/warehouse/calibrate/layout')}
          className="mb-5 px-4 py-2 rounded-lg bg-gray-800 border border-gray-600 text-gray-200 text-sm hover:border-blue-500">
          Picking layout from the measurements…
        </button>
      )}
      {err && <div className="text-red-400 text-sm mb-3">{err}</div>}
      {!rows ? <div className="text-gray-500 text-sm">Loading…</div> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map(a => (
            <div key={a.id} className="rounded-xl border border-gray-700 bg-gray-800/30 p-3">
              <div className="flex items-baseline justify-between mb-2">
                <div className="text-lg font-semibold text-white">{a.name}</div>
                <button onClick={() => navigate(`/warehouse/calibrate/${encodeURIComponent(a.id)}`)}
                  className="text-xs text-gray-400 hover:text-blue-300">Check codes</button>
              </div>
              <div className="flex gap-2">
                {faceBtn(faces.find(f => f.aisle === a.name && f.side === 'left'))}
                {faceBtn(faces.find(f => f.aisle === a.name && f.side === 'right'))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Manager/admin: rebuild the picking layout's stops from the measured section positions. */
export function LayoutFromSections() {
  const navigate = useNavigate();
  const [data, setData] = useState<Record<string, any> | null>(null);
  const [pick, setPick] = useState<Record<string, boolean>>({});
  const [open, setOpen] = useState<string | null>(null);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const load = () => jget(`${API}/warehouse/layout/from-sections`).then(d => {
    setData(d.aisles);
    setPick(Object.fromEntries(Object.entries(d.aisles).map(([n, v]: [string, any]) => [n, v.lost_with_stock.length === 0])));
  }).catch(e => setMsg(e.message));
  useEffect(() => { load(); }, []);
  if (!data) return <div className="p-6 text-gray-500">{msg || 'Loading…'}</div>;
  const chosen = Object.keys(pick).filter(k => pick[k]);
  return (
    <div className="p-6 max-w-4xl">
      <button onClick={() => navigate('/settings/warehouse/tools')} className="text-gray-400 hover:text-white text-sm">← Settings · Warehouse</button>
      <span className="text-gray-600 text-sm mx-2">·</span>
      <button onClick={() => navigate('/warehouse/calibrate')} className="text-gray-400 hover:text-white text-sm">Calibrate</button>
      <h1 className="text-xl font-semibold text-white mt-1 mb-1">Picking layout from the measurements</h1>
      <p className="text-sm text-gray-400 mb-4">
        Each aisle's stops are rebuilt from where the measured sections stand: sections on the two sides that overlap
        face each other; one metal bay faces the pallet sections beside it. The previous layout is kept. An aisle where
        a stocked code would lose its stop is unticked. Place that code first.
      </p>
      <table className="w-full text-sm mb-4">
        <thead className="text-left text-gray-500 border-b border-gray-800">
          <tr><th className="py-2 w-8"></th><th>Aisle</th><th>Stops</th><th>Codes</th><th>Stocked codes that lose their stop</th><th></th></tr>
        </thead>
        <tbody>
          {Object.entries(data).map(([n, v]: [string, any]) => (
            <React.Fragment key={n}>
              <tr className="border-b border-gray-800/60 text-gray-200">
                <td className="py-1.5"><input type="checkbox" checked={!!pick[n]} onChange={e => setPick(p => ({ ...p, [n]: e.target.checked }))} /></td>
                <td className="font-semibold">{n}</td>
                <td>{v.stops_before} → {v.stops.length}</td>
                <td>{v.old_codes} → {v.new_codes}</td>
                <td className={v.lost_with_stock.length ? 'text-amber-300' : 'text-gray-500'}>
                  {v.lost_with_stock.length ? v.lost_with_stock.map((x: any) => `${displayLocation(x.code)} (${x.pieces} pcs)`).join(', ') : 'none'}</td>
                <td><button onClick={() => setOpen(open === n ? null : n)} className="text-xs text-blue-300">{open === n ? 'hide' : 'stops'}</button></td>
              </tr>
              {open === n && (
                <tr><td colSpan={6} className="py-2">
                  <div className="font-mono text-xs text-gray-400 max-h-64 overflow-auto">
                    {v.stops.map((s: any, i: number) => (
                      <div key={i}>{String(i + 1).padStart(3, ' ')}  {s.left.map(displayLocation).join(' ') || '—'}  |  {s.right.map(displayLocation).join(' ') || '—'}</div>
                    ))}
                  </div>
                </td></tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      {msg && <div className="text-sm text-gray-300 mb-3">{msg}</div>}
      <button disabled={busy || !chosen.length} onClick={async () => {
        setBusy(true); setMsg('');
        const r = await authFetch(`${API}/warehouse/layout/from-sections/apply`, { method: 'POST', body: JSON.stringify({ aisles: chosen }) });
        const d = await r.json().catch(() => ({}));
        setBusy(false);
        if (!r.ok) { setMsg(d.detail || 'Apply failed'); return; }
        setMsg(`Applied: ${Object.keys(d.aisles).join(', ')}. The previous layout is kept in the history.`);
        load();
      }} className="px-5 py-3 rounded-xl bg-emerald-600 text-white font-semibold disabled:opacity-40">
        Apply {chosen.length} aisle{chosen.length === 1 ? '' : 's'} to the picking layout
      </button>
    </div>
  );
}

// ─── who is walking ──────────────────────────────────────────────────────────

// "Who is walking?" moved to pp/WhoIsWorking.tsx (shared with the Inventory count, 2026-10-06)
function WhoIsWalking({ onPick, onCancel }: { onPick: (u: string) => void; onCancel: () => void }) {
  return <WhoIsWorking title="Who is walking?" onPick={p => onPick(p.username)} onCancel={onCancel} />;
}

// ─── the walk ────────────────────────────────────────────────────────────────

export default function CalibrateWalk() {
  const { aisleId = '' } = useParams();
  const navigate = useNavigate();
  const [view, setView] = useState<AisleView | null>(null);
  const [walkId, setWalkId] = useState<number | null>(null);
  const [needActor, setNeedActor] = useState(false);
  const [actor, setActor] = useState<string | null>(null);
  const [stops, setStops] = useState<WalkStop[]>([]);
  const [units, setUnits] = useState<Record<string, WalkUnit>>({});
  const [pos, setPos] = useState(0);
  const [width, setWidth] = useState<string>('');
  const [err, setErr] = useState('');
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [preview, setPreview] = useState<any | null>(null);
  const [applied, setApplied] = useState<any | null>(null);
  const [edit, setEdit] = useState<{ side: Side; i: number; mode: 'wrong' | 'add' } | null>(null);
  const [editText, setEditText] = useState('');
  const loaded = useRef(false);

  // 1. the aisle as the model knows it
  useEffect(() => {
    jget(`${API}/warehouse/aisles/${encodeURIComponent(aisleId)}`).then(setView).catch(e => setErr(e.message));
  }, [aisleId]);

  // 2. start or resume the walk (a tablet asks who is walking first)
  const start = useCallback(async (who: string | null) => {
    setErr('');
    const r = await authFetch(`${API}/warehouse/walks`, {
      method: 'POST', body: JSON.stringify({ aisle_id: aisleId, actor: who }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { setErr(d.detail || 'Could not start the walk'); return; }
    setWalkId(d.walk.id);
    setActor(d.walk.actor);
    if (d.walk.aisle_width_cm) setWidth(String(d.walk.aisle_width_cm));
    if (d.resumed && (d.walk.stops || []).length) {
      setStops(d.walk.stops);
      setUnits((d.walk.data && d.walk.data.units) || {});
      setPos(Math.min((d.walk.data && d.walk.data.position) || 0, d.walk.stops.length - 1));
    } else if (view) {
      initFromView(view);
    }
    loaded.current = true;
  }, [aisleId, view]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!view || walkId) return;
    if (isDevice()) setNeedActor(true); else start(null);
  }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

  function initFromView(v: AisleView) {
    const us: Record<string, WalkUnit> = {};
    for (const [id, u] of Object.entries(v.units || {})) {
      us[id] = { setup: u.setup, kind: u.kind, turn: u.turn, system: u.system, note: u.note || '', side: null };
    }
    const st: WalkStop[] = v.stops.map((s, i) => ({
      left: s.left.map(x => ({ code: x.code, status: null, actual: null, unit: x.unit_id })),
      right: s.right.map(x => ({ code: x.code, status: null, actual: null, unit: x.unit_id })),
      gaps: v.gaps.filter(g => g.stop_index === i).map(g => ({ side: g.side, reason: g.reason, note: g.note || '' })),
    }));
    setUnits(us); setStops(st); setPos(0);
  }

  // autosave — the whole state, debounced
  useEffect(() => {
    if (!walkId || !loaded.current || applied) return;
    setSaveState('saving');
    const t = setTimeout(async () => {
      const w = parseInt(width, 10);
      const r = await authFetch(`${API}/warehouse/walks/${walkId}`, {
        method: 'PUT',
        body: JSON.stringify({ stops, units, position: pos, aisle_width_cm: Number.isFinite(w) ? w : null }),
      }).catch(() => null);
      setSaveState(r && r.ok ? 'saved' : 'error');
    }, 1200);
    return () => clearTimeout(t);
  }, [stops, units, pos, width, walkId, applied]);

  const stockOf = useMemo(() => {
    const m: Record<string, Stock> = {};
    if (view) {
      for (const s of view.stops) for (const x of [...s.left, ...s.right]) m[x.code] = x.stock;
      for (const u of view.unplaced) m[u.code] = u.stock;
    }
    return m;
  }, [view]);

  const face = (side: Side) => (view ? view.faces[side] : null);
  const setupsFor = (side: Side) => (face(side)?.kind === 'metal' ? METAL_SETUPS : CARTON_SETUPS);
  const footprint = (key: string | null) => {
    const s = key ? units[key]?.setup : null;
    return (s && view?.setups[s]?.footprint) || 1;
  };

  // walk order of one side: [(stopIndex, sectionIndex)] over live stops and live sections
  const order = (st: WalkStop[], side: Side) => {
    const out: [number, number][] = [];
    st.forEach((s, si) => { if (!s.removed) s[side].forEach((x, xi) => { if (x.status !== 'missing') out.push([si, xi]); }); });
    return out;
  };

  /** The unit a newly confirmed section belongs to: it continues the previous section's unit
   *  on this side until that unit's footprint is full, then a new unit of the same setup. */
  function autoUnit(st: WalkStop[], us: Record<string, WalkUnit>, side: Side, si: number, xi: number): [string, Record<string, WalkUnit>] {
    const seq = order(st, side);
    const me = seq.findIndex(([a, b]) => a === si && b === xi);
    const prev = me > 0 ? seq[me - 1] : null;
    const prevKey = prev ? st[prev[0]][side][prev[1]].unit : null;
    if (prevKey && us[prevKey]) {
      const count = seq.filter(([a, b]) => st[a][side][b].unit === prevKey).length;
      const fp = (us[prevKey].setup && view?.setups[us[prevKey].setup!]?.footprint) || 1;
      if (count < fp) return [prevKey, us];
      const k = newKey();
      return [k, { ...us, [k]: { ...us[prevKey], note: '', side } }];
    }
    const k = newKey();
    const f = face(side);
    return [k, { ...us, [k]: { setup: null, kind: f?.kind === 'metal' ? 'bay' : 'pallet', turn: null,
                                system: f?.system || null, note: '', side } }];
  }

  function setSection(side: Side, xi: number, patch: Partial<WalkSection>, si = pos) {
    setStops(prev => {
      const st = prev.map(s => ({ ...s, left: [...s.left], right: [...s.right] }));
      const cur = { ...st[si][side][xi], ...patch };
      st[si][side][xi] = cur;
      if (cur.status && cur.status !== 'missing' && !cur.unit) {
        const [k, us] = autoUnit(st, units, side, si, xi);
        st[si][side][xi] = { ...cur, unit: k };
        if (us !== units) setUnits(us);
      }
      return st;
    });
  }

  function confirmAll(next: boolean) {
    setStops(prev => {
      const st = prev.map(s => ({ ...s, left: [...s.left], right: [...s.right] }));
      let us = units;
      for (const side of ['left', 'right'] as Side[]) {
        st[pos][side].forEach((x, xi) => {
          if (x.status) return;
          let cur: WalkSection = { ...x, status: 'ok' };
          st[pos][side][xi] = cur;
          if (!cur.unit) {
            const [k, u2] = autoUnit(st, us, side, pos, xi);
            us = u2; st[pos][side][xi] = { ...cur, unit: k };
          }
        });
      }
      if (us !== units) setUnits(us);
      return st;
    });
    if (next) setPos(p => Math.min(p + 1, stops.length - 1));
  }

  /** A new unit starts at this section; the old unit's later sections follow the new one. */
  function newUnitHere(side: Side, xi: number) {
    const x = stops[pos][side][xi];
    const old = x.unit;
    const k = newKey();
    const base = old && units[old] ? units[old] : { setup: null, kind: face(side)?.kind === 'metal' ? 'bay' : 'pallet',
                                                    turn: null, system: face(side)?.system || null, note: '', side };
    setUnits(u => ({ ...u, [k]: { ...base, note: '', side } }));
    setStops(prev => {
      const st = prev.map(s => ({ ...s, left: [...s.left], right: [...s.right] }));
      const seq = order(st, side);
      const me = seq.findIndex(([a, b]) => a === pos && b === xi);
      seq.forEach(([a, b], i) => {
        if (i === me || (i > me && old && st[a][side][b].unit === old)) st[a][side][b] = { ...st[a][side][b], unit: k };
      });
      if (me < 0) st[pos][side][xi] = { ...st[pos][side][xi], unit: k };
      return st;
    });
  }

  function setUnitSetup(key: string, setup: string) {
    setUnits(u => ({ ...u, [key]: { ...u[key], setup, turn: TURN_OF[setup] || null,
                                     kind: view?.setups[setup]?.kind || u[key]?.kind || null } }));
  }

  function moveSection(side: Side, xi: number, dir: -1 | 1) {
    const to = pos + dir;
    if (to < 0 || to >= stops.length) return;
    setStops(prev => {
      const st = prev.map(s => ({ ...s, left: [...s.left], right: [...s.right] }));
      const [x] = st[pos][side].splice(xi, 1);
      if (dir === 1) st[to][side].unshift(x); else st[to][side].push(x);
      return st;
    });
  }

  function addSection(side: Side, code: string) {
    const c = normLocation(code);
    if (!c) return;
    setStops(prev => {
      const st = prev.map(s => ({ ...s, left: [...s.left], right: [...s.right] }));
      st[pos][side].push({ code: c, status: 'added', actual: c, unit: null });
      const xi = st[pos][side].length - 1;
      const [k, us] = autoUnit(st, units, side, pos, xi);
      st[pos][side][xi] = { ...st[pos][side][xi], unit: k };
      if (us !== units) setUnits(us);
      return st;
    });
  }

  function toggleGap(side: Side, reason: string) {
    setStops(prev => prev.map((s, i) => {
      if (i !== pos) return s;
      const has = s.gaps.find(g => g.side === side && g.reason === reason);
      return { ...s, gaps: has ? s.gaps.filter(g => g !== has) : [...s.gaps, { side, reason, note: '' }] };
    }));
  }

  function insertStop() {
    setStops(prev => [...prev.slice(0, pos + 1), { left: [], right: [], gaps: [], added: true }, ...prev.slice(pos + 1)]);
    setPos(p => p + 1);
  }
  function toggleRemoveStop() {
    setStops(prev => prev.map((s, i) => (i === pos ? { ...s, removed: !s.removed } : s)));
  }

  async function finish() {
    setErr('');
    // flush the autosave first, then ask for the preview
    const w = parseInt(width, 10);
    await authFetch(`${API}/warehouse/walks/${walkId}`, {
      method: 'PUT', body: JSON.stringify({ stops, units, position: pos, aisle_width_cm: Number.isFinite(w) ? w : null }),
    });
    try { setPreview((await jget(`${API}/warehouse/walks/${walkId}/preview`)).change); }
    catch (e: any) { setErr(e.message); }
  }

  async function apply() {
    setErr('');
    const r = await authFetch(`${API}/warehouse/walks/${walkId}/apply`, { method: 'POST' });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { setErr(d.detail || 'Apply failed'); return; }
    setApplied(d.summary);
  }

  // unit labels: an existing unit shows its id; a new one "new pallet 3" by order on its side
  const unitLabel = useMemo(() => {
    const m: Record<string, string> = {};
    for (const side of ['left', 'right'] as Side[]) {
      let n = 0;
      for (const [si, xi] of order(stops, side)) {
        const k = stops[si][side][xi].unit;
        if (!k || m[k]) continue;
        if (!k.startsWith('n')) m[k] = k;
        else m[k] = `new ${units[k]?.kind === 'bay' ? 'bay' : 'pallet'} ${++n}`;
      }
    }
    return m;
  }, [stops, units]); // eslint-disable-line react-hooks/exhaustive-deps

  if (err && !view) return <div className="p-6 text-red-400">{err}</div>;
  if (!view) return <div className="p-6 text-gray-500">Loading…</div>;
  if (needActor && !walkId) return <WhoIsWalking onPick={u => { setNeedActor(false); start(u); }} onCancel={() => navigate('/warehouse/calibrate')} />;
  if (!walkId || !stops.length) return <div className="p-6 text-gray-500">{err || 'Starting the walk…'}</div>;

  if (applied) return (
    <div className="p-6 max-w-2xl">
      <h2 className="text-xl font-semibold text-emerald-300 mb-3">{view.aisle.name} applied</h2>
      <ul className="text-sm text-gray-300 space-y-1">
        <li>{applied.sections} sections · {applied.stops_changed} stops changed · {applied.gaps} gaps</li>
        <li>{applied.units_new} new units · {applied.units_paired} paired with the other face · {applied.units_updated} updated</li>
        {applied.sections_gone > 0 && <li>{applied.sections_gone} sections left the model</li>}
      </ul>
      <button onClick={() => navigate('/warehouse/calibrate')} className="mt-5 px-4 py-2 rounded-lg bg-blue-600 text-white">Next aisle</button>
    </div>
  );

  if (preview) return <Preview p={preview} canApply={role() === 'admin' || role() === 'manager'}
                               err={err} onBack={() => setPreview(null)} onApply={apply} />;

  const stop = stops[pos];
  const liveCount = stops.filter(s => !s.removed).length;
  const pending = stops.reduce((n, s) => n + (s.removed ? 0 : [...s.left, ...s.right].filter(x => !x.status).length), 0);

  const renderSide = (side: Side) => {
    const f = face(side);
    return (
      <div className="flex-1 min-w-0">
        <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">
          {side === 'left' ? 'Left' : 'Right'} · {f ? `${f.letter} face (${f.kind === 'metal' ? f.system : 'pallets'})` : 'no face'}
        </div>
        {stop[side].length === 0 && <div className="text-gray-600 text-sm mb-2">No section here</div>}
        {stop[side].map((x, xi) => {
          const code = x.status === 'wrong' || x.status === 'added' ? x.actual || x.code : x.code;
          const st = stockOf[normLocation(code)] || stockOf[x.code];
          const u = x.unit ? units[x.unit] : null;
          const tone = x.status === 'ok' || x.status === 'added' ? 'border-emerald-600 bg-emerald-900/20'
            : x.status === 'wrong' ? 'border-amber-500 bg-amber-900/20'
            : x.status === 'missing' ? 'border-red-700 bg-red-900/10 opacity-60' : 'border-gray-700 bg-gray-800/50';
          return (
            <div key={xi} className={`rounded-xl border-2 ${tone} p-3 mb-3`}>
              <div className="flex items-baseline justify-between gap-2">
                <div className="text-3xl font-bold text-white">
                  {displayLocation(code)}
                  {x.status === 'wrong' && <span className="text-sm text-amber-300 ml-2 line-through">{displayLocation(x.code)}</span>}
                </div>
                <div className="flex gap-1">
                  <button title="Faces the previous stop" onClick={() => moveSection(side, xi, -1)} className="px-2 py-1 rounded bg-gray-700 text-gray-200 text-sm">↑</button>
                  <button title="Faces the next stop" onClick={() => moveSection(side, xi, 1)} className="px-2 py-1 rounded bg-gray-700 text-gray-200 text-sm">↓</button>
                </div>
              </div>
              <div className="text-sm text-gray-400 mt-1">
                {st && st.pieces > 0 ? `${st.pieces} pcs · ${st.styles} style${st.styles === 1 ? '' : 's'}${st.top_brand ? ` · ${st.top_brand}` : ''}` : 'no stock recorded'}
              </div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => setSection(side, xi, { status: 'ok', actual: null })}
                  className={`flex-1 py-3 rounded-lg text-lg font-semibold ${x.status === 'ok' ? 'bg-emerald-600 text-white' : 'bg-gray-700 text-gray-100'}`}>✓</button>
                <button onClick={() => { setEdit({ side, i: xi, mode: 'wrong' }); setEditText(x.actual || ''); }}
                  className={`px-3 py-3 rounded-lg text-sm ${x.status === 'wrong' ? 'bg-amber-600 text-white' : 'bg-gray-700 text-gray-200'}`}>Other code</button>
                <button onClick={() => setSection(side, xi, { status: 'missing', actual: null, unit: null })}
                  className={`px-3 py-3 rounded-lg text-sm ${x.status === 'missing' ? 'bg-red-700 text-white' : 'bg-gray-700 text-gray-200'}`}>Not here</button>
              </div>
              {x.unit && u && (
                <div className="mt-3 border-t border-gray-700 pt-2">
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <span>On <b className="text-gray-200">{unitLabel[x.unit] || x.unit}</b>{u.setup ? ` · ${SETUP_LABEL[u.setup] || u.setup}` : ' · setup?'}</span>
                    <button onClick={() => newUnitHere(side, xi)} className="text-blue-300 hover:text-blue-200">New {f?.kind === 'metal' ? 'bay' : 'pallet'} starts here</button>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {[...setupsFor(side), 'other'].map(s => (
                      <button key={s} onClick={() => setUnitSetup(x.unit!, s)}
                        className={`px-3 py-2 rounded-lg text-sm ${u.setup === s ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-200'}`}>{SETUP_LABEL[s]}</button>
                    ))}
                  </div>
                  {(u.setup === 'other' || u.setup === 'tee_special_B') && (
                    <input value={u.note} onChange={e => { const v = e.target.value; setUnits(us => ({ ...us, [x.unit!]: { ...us[x.unit!], note: v } })); }}
                      placeholder="What is special about it" className="mt-2 w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm text-white" />
                  )}
                </div>
              )}
            </div>
          );
        })}
        <div className="flex flex-wrap gap-1">
          <button onClick={() => { setEdit({ side, i: -1, mode: 'add' }); setEditText(''); }}
            className="px-3 py-2 rounded-lg bg-gray-800 text-gray-300 text-sm">+ Section</button>
          {view.gap_reasons.map(r => {
            const on = stop.gaps.some(g => g.side === side && g.reason === r);
            return <button key={r} onClick={() => toggleGap(side, r)}
              className={`px-2 py-2 rounded-lg text-xs ${on ? 'bg-fuchsia-700 text-white' : 'bg-gray-800 text-gray-400'}`}>{on ? '● ' : ''}{r}</button>;
          })}
        </div>
        {side === edit?.side && (
          <div className="mt-2 flex gap-2">
            <input autoFocus value={editText} onChange={e => setEditText(e.target.value)} placeholder="e.g. M 23"
              className="flex-1 bg-gray-900 border border-gray-600 rounded px-3 py-2 text-lg text-white" />
            <button onClick={() => {
              const p = parseLocation(editText);
              if (p.kind === 'invalid' || p.kind === 'none') { setErr(`"${editText}" is not a location`); return; }
              setErr('');
              if (edit.mode === 'add') addSection(side, p.code); else setSection(side, edit.i, { status: 'wrong', actual: p.code });
              setEdit(null);
            }} className="px-4 rounded bg-blue-600 text-white">OK</button>
            <button onClick={() => setEdit(null)} className="px-3 rounded bg-gray-700 text-gray-200">✕</button>
          </div>
        )}
      </div>
    );
  };

  const unplaced = view.unplaced.filter(u => !stops.some(s => [...s.left, ...s.right].some(x => (x.actual || x.code) === u.code)));

  return (
    <div className="p-3 sm:p-4 max-w-5xl pb-28 md:pb-4">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div>
          <button onClick={() => navigate('/warehouse/calibrate')} className="text-gray-400 hover:text-white text-sm">← Aisles</button>
          <h1 className="text-2xl font-bold text-white">{view.aisle.name} <span className="text-base font-normal text-gray-400">· {actor}</span></h1>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <label className="text-gray-400">Aisle width
            <input value={width} onChange={e => setWidth(e.target.value.replace(/\D/g, ''))} inputMode="numeric"
              className="ml-2 w-16 bg-gray-900 border border-gray-700 rounded px-2 py-1 text-white" /> cm</label>
          <span className={saveState === 'error' ? 'text-red-400' : 'text-gray-500'}>
            {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Saved' : saveState === 'error' ? 'Not saved — check the connection' : ''}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <button onClick={() => setPos(p => Math.max(0, p - 1))} disabled={pos === 0}
          className="px-4 py-3 rounded-lg bg-gray-700 text-white disabled:opacity-30">← Prev</button>
        <div className="flex-1 text-center">
          <div className="text-white text-lg font-semibold">Stop {pos + 1} / {stops.length}{stop.removed ? ' — removed' : stop.added ? ' — added' : ''}</div>
          <div className="text-xs text-gray-500">{pending} section(s) still to confirm · {liveCount} stops</div>
        </div>
        <button onClick={() => setPos(p => Math.min(stops.length - 1, p + 1))} disabled={pos >= stops.length - 1}
          className="px-4 py-3 rounded-lg bg-gray-700 text-white disabled:opacity-30">Next →</button>
      </div>

      {err && <div className="text-red-400 text-sm mb-2">{err}</div>}

      <div className={`flex flex-col md:flex-row gap-4 ${stop.removed ? 'opacity-40 pointer-events-none' : ''}`}>
        {renderSide('left')}
        {renderSide('right')}
      </div>

      <div className="flex flex-wrap gap-2 mt-4 fixed md:static bottom-0 left-0 right-0 z-30 p-3 md:p-0 bg-[#0f1117] md:bg-transparent border-t md:border-0 border-gray-800">
        <button onClick={() => confirmAll(true)} className="flex-1 min-w-[200px] py-4 rounded-xl bg-emerald-600 text-white text-lg font-semibold">
          ✓ All correct — next stop
        </button>
        <button onClick={insertStop} className="px-4 py-3 rounded-xl bg-gray-800 text-gray-200 text-sm">+ Stop after this</button>
        <button onClick={toggleRemoveStop} className="px-4 py-3 rounded-xl bg-gray-800 text-gray-200 text-sm">{stop.removed ? 'Keep this stop' : 'Remove this stop'}</button>
        <button onClick={finish} className="px-5 py-3 rounded-xl bg-blue-600 text-white font-semibold">Finish…</button>
      </div>

      {unplaced.length > 0 && (
        <div className="mt-6 rounded-xl border border-gray-700 p-3">
          <div className="text-sm text-gray-300 mb-2">Stock on these letters at codes that are in no stop — tap to put one at this stop:</div>
          <div className="flex flex-wrap gap-2">
            {unplaced.map(u => {
              const side: Side | null = parseLocation(u.code).letter === face('left')?.letter ? 'left'
                : parseLocation(u.code).letter === face('right')?.letter ? 'right' : null;
              return side && (
                <button key={u.code} onClick={() => addSection(side, u.code)} className="px-3 py-2 rounded-lg bg-gray-800 text-gray-200 text-sm">
                  {displayLocation(u.code)} <span className="text-gray-500">{u.stock.pieces} pcs</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── the preview ─────────────────────────────────────────────────────────────

function Preview({ p, canApply, err, onBack, onApply }: { p: any; canApply: boolean; err: string; onBack: () => void; onApply: () => void }) {
  const [busy, setBusy] = useState(false);
  return (
    <div className="p-5 max-w-4xl">
      <button onClick={onBack} className="text-gray-400 hover:text-white text-sm">← Back to the walk</button>
      <h2 className="text-xl font-semibold text-white mt-1 mb-3">{p.aisle.name} — what Apply changes</h2>
      <div className="text-sm text-gray-300 mb-3">
        {p.counts.sections} sections · {p.stops_before} → {p.stops_after} stops ({p.counts.stops_changed} changed) ·
        {' '}{p.counts.units_new} new units · {p.counts.units_paired} paired · {p.counts.units_updated} updated · {p.counts.gaps} gaps
        {p.sections_gone.length > 0 && <> · <span className="text-amber-300">{p.sections_gone.length} leave the model ({p.sections_gone.map(displayLocation).join(', ')})</span></>}
      </div>
      {p.errors.length > 0 && <div className="rounded-lg bg-red-900/30 border border-red-700 p-3 mb-3 text-sm text-red-200">
        {p.errors.map((e: string, i: number) => <div key={i}>{e}</div>)}</div>}
      {p.warnings.length > 0 && <div className="rounded-lg bg-amber-900/20 border border-amber-700 p-3 mb-3 text-sm text-amber-200">
        {p.warnings.map((e: string, i: number) => <div key={i}>{e}</div>)}</div>}

      <h3 className="text-sm uppercase tracking-wider text-gray-500 mt-4 mb-1">Numbering check</h3>
      {Object.entries(p.scheme).map(([letter, fs]: [string, any]) => (
        <div key={letter} className="text-sm mb-1">
          <b className="text-white">{letter}</b>{' '}
          {fs.length === 0 ? <span className="text-emerald-300">follows the scheme</span>
            : <span className="text-gray-300">{fs.map((f: any) => f.text).join(' · ')}</span>}
        </div>
      ))}

      <h3 className="text-sm uppercase tracking-wider text-gray-500 mt-4 mb-1">Units</h3>
      <div className="text-sm text-gray-300 space-y-0.5 max-h-64 overflow-auto">
        {p.units.map((u: any, i: number) => (
          <div key={i}>
            <span className={u.action === 'create' ? 'text-emerald-300' : u.action === 'pair' ? 'text-blue-300' : 'text-gray-400'}>
              {u.action === 'create' ? 'new' : u.action}</span>{' '}
            {u.id || ''} {SETUP_LABEL[u.setup] || u.setup} — {u.codes.map(displayLocation).join(', ')}
          </div>
        ))}
      </div>

      {p.layout_diff.length > 0 && <>
        <h3 className="text-sm uppercase tracking-wider text-gray-500 mt-4 mb-1">Picking layout changes</h3>
        <div className="text-xs text-gray-400 space-y-0.5 max-h-48 overflow-auto font-mono">
          {p.layout_diff.map((d: any, i: number) => (
            <div key={i}>stop {d.stop}: {d.before.left.join(' ')} | {d.before.right.join(' ')} → {d.after ? `${d.after.left.join(' ')} | ${d.after.right.join(' ')}` : 'removed'}</div>
          ))}
        </div>
      </>}

      {err && <div className="text-red-400 text-sm mt-3">{err}</div>}
      <div className="mt-5 flex gap-3">
        {canApply ? (
          <button disabled={busy || p.errors.length > 0} onClick={async () => { setBusy(true); await onApply(); setBusy(false); }}
            className="px-5 py-3 rounded-xl bg-emerald-600 text-white font-semibold disabled:opacity-40">Apply to the warehouse model</button>
        ) : (
          <div className="text-sm text-gray-400">The walk is saved. A manager applies it from Warehouse → Calibrate.</div>
        )}
      </div>
    </div>
  );
}
